import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Beaker, CheckCircle2, XCircle, AlertCircle, Loader2, FileText, ChevronDown, ChevronRight, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function PublisherSandboxPage() {
  const [agentUrl, setAgentUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [runId, setRunId] = useState(null);
  const [run, setRun] = useState(null);
  const [error, setError] = useState("");
  const [testSuite, setTestSuite] = useState([]);
  const [expandedTests, setExpandedTests] = useState({});
  const pollRef = useRef(null);

  useEffect(() => {
    document.title = "Sandbox testowy Publisher API | Epidemos";
    axios.get(`${BACKEND_URL}/api/v1/sandbox/test-suite`)
      .then(r => setTestSuite(r.data.tests || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!runId) return;
    const poll = async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/v1/sandbox/runs/${runId}`);
        const data = r.data.run;
        setRun(data);
        if (data.status === "done") {
          clearInterval(pollRef.current);
          setSubmitting(false);
        }
      } catch (e) {
        clearInterval(pollRef.current);
        setError("Błąd pobierania statusu");
        setSubmitting(false);
      }
    };
    poll();
    pollRef.current = setInterval(poll, 2000);
    return () => clearInterval(pollRef.current);
  }, [runId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setRun(null);
    setRunId(null);
    const url = agentUrl.trim().replace(/\/+$/, "");
    if (!url.match(/^https?:\/\//)) {
      setError("URL musi zaczynać się od http:// lub https://");
      return;
    }
    if (!apiKey.trim()) {
      setError("Klucz API wymagany");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/v1/sandbox/verify`, {
        agent_url: url, api_key: apiKey.trim(), suite_version: "v2",
      });
      setRunId(r.data.run_id);
    } catch (e) {
      setError(e.response?.data?.detail || "Błąd uruchomienia testów");
      setSubmitting(false);
    }
  };

  const results = run?.results || run?.results_partial || [];
  const groupedByCategory = results.reduce((acc, r) => {
    (acc[r.category] = acc[r.category] || []).push(r);
    return acc;
  }, {});

  const StatusIcon = ({ test }) => {
    if (test.pass) return <CheckCircle2 className="w-5 h-5 text-green-600" />;
    if (test.soft) return <AlertCircle className="w-5 h-5 text-amber-500" />;
    return <XCircle className="w-5 h-5 text-red-600" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header onLogin={() => window.location.href = "/login"} />

      <section className="pt-24 pb-10 bg-gradient-to-br from-purple-600 to-blue-700 text-white">
        <div className="max-w-none mx-auto px-6">
          <div className="flex items-center gap-3 mb-3 text-purple-200">
            <Beaker className="w-5 h-5" />
            <span className="text-sm font-semibold">Sandbox Publisher API v2</span>
          </div>
          <h1 className="text-4xl font-bold mb-3">Testy wydawcy</h1>
          <p className="text-lg text-purple-100 max-w-none mb-6">
            Przetestuj implementację API swojego portalu zanim wyślesz dane administratorowi Epidemos.
            ~15 testów uderzy w Twoje endpointy i wskaże co poprawić.
          </p>
          <Link to="/api-docs" className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-semibold transition border border-white/30">
            <FileText className="w-4 h-4" /> Najpierw przeczytaj dokumentację →
          </Link>
        </div>
      </section>

      <section className="max-w-none mx-auto px-6 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* FORMULARZ */}
          <div className="md:col-span-1 bg-white rounded-xl shadow-sm border border-slate-200 p-6 h-fit sticky top-24">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Uruchom testy</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">URL portalu</label>
                <input type="text" value={agentUrl} onChange={e => setAgentUrl(e.target.value)}
                       placeholder="https://moj-portal.pl" required
                       className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none text-sm" />
                <p className="text-xs text-slate-500 mt-1">Bez końcowego /api — sam dopisze ścieżki</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Klucz API (testowy)</label>
                <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)}
                       placeholder="••••••••••••" required
                       className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 outline-none text-sm font-mono" />
                <p className="text-xs text-slate-500 mt-1">Klucz musi mieć scope'y: write, media, comments, ratings, read</p>
              </div>
              <button type="submit" disabled={submitting}
                      className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white font-semibold rounded-lg transition flex items-center justify-center gap-2">
                {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" />Testuję…</>) : "Uruchom testy"}
              </button>
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>
              )}
              <p className="text-xs text-slate-500 leading-relaxed pt-3 border-t border-slate-100">
                Testy są <strong>non-destructive</strong> ale tworzą posty z prefiksem <code className="bg-slate-100 px-1 rounded">[SANDBOX-TEST]</code>.
                Wyczyść je po zakończeniu testów.
              </p>
              <p className="text-xs text-slate-500">
                Limit: <strong>5 runs/h</strong> per IP.
              </p>
            </form>
          </div>

          {/* WYNIKI / SUITE */}
          <div className="md:col-span-2">
            {!run && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-3">Co zostanie sprawdzone ({testSuite.length} testów)</h2>
                <div className="space-y-2 text-sm">
                  {Object.entries(testSuite.reduce((acc, t) => { (acc[t.category] = acc[t.category] || []).push(t); return acc; }, {}))
                    .map(([cat, tests]) => (
                      <div key={cat} className="border border-slate-200 rounded-lg p-3">
                        <h3 className="font-semibold text-slate-900 capitalize mb-1">{cat} ({tests.length})</h3>
                        <ul className="text-slate-600 space-y-0.5 ml-3">
                          {tests.map(t => (
                            <li key={t.id} className="flex items-center gap-2 text-xs">
                              <span className="font-mono text-slate-400">{t.method}</span>
                              <span className="font-mono text-slate-500">{t.path}</span>
                              <span>— {t.name}</span>
                              {t.soft && <span className="text-amber-600 text-[10px]">(soft)</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {run && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                {/* Header status */}
                <div className="flex items-start justify-between mb-6 pb-4 border-b border-slate-100">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 mb-1">Wynik testów</h2>
                    <p className="text-xs text-slate-500 font-mono">{runId}</p>
                  </div>
                  <div className="text-right">
                    {run.status === "running" && (
                      <div className="flex items-center gap-2 text-blue-600">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="font-semibold">{run.progress || 0}%</span>
                      </div>
                    )}
                    {run.status === "done" && (
                      <div>
                        <div className={`text-3xl font-bold ${run.ready_for_production ? "text-green-600" : "text-amber-600"}`}>
                          {run.score}%
                        </div>
                        <div className="text-xs text-slate-500">
                          {run.hard_pass}/{run.hard_total} hard, {run.soft_pass}/{run.soft_total} soft
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Verdict */}
                {run.status === "done" && (
                  <div className={`p-4 rounded-lg mb-6 ${run.ready_for_production
                    ? "bg-green-50 border border-green-200 text-green-900"
                    : "bg-amber-50 border border-amber-200 text-amber-900"}`}>
                    <div className="flex items-center gap-2 font-bold mb-1">
                      {run.ready_for_production
                        ? <><CheckCircle2 className="w-5 h-5" /> READY FOR PRODUCTION</>
                        : <><AlertCircle className="w-5 h-5" /> WYMAGA POPRAWY</>}
                    </div>
                    <p className="text-sm">
                      {run.ready_for_production
                        ? "Wszystkie wymagane testy przeszły. Wyślij URL portalu + klucz administratorowi epidemos."
                        : "Część testów hard nie przeszła. Popraw zaznaczone endpointy i uruchom testy ponownie."}
                    </p>
                  </div>
                )}

                {/* Wyniki per kategoria */}
                {Object.entries(groupedByCategory).map(([cat, tests]) => (
                  <div key={cat} className="mb-4">
                    <h3 className="text-sm font-bold uppercase text-slate-500 tracking-wide mb-2">{cat}</h3>
                    <div className="space-y-1">
                      {tests.map(t => (
                        <div key={t.test_id}
                             className={`border rounded-lg overflow-hidden ${t.pass
                               ? "border-green-200 bg-green-50/30"
                               : t.soft
                                 ? "border-amber-200 bg-amber-50/30"
                                 : "border-red-200 bg-red-50/30"}`}>
                          <button onClick={() => setExpandedTests({ ...expandedTests, [t.test_id]: !expandedTests[t.test_id] })}
                                  className="w-full flex items-center gap-3 p-3 text-left hover:bg-black/5">
                            <StatusIcon test={t} />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-slate-900 text-sm">{t.name}</div>
                              <div className="text-xs text-slate-500 mt-0.5 flex gap-3">
                                <span>HTTP {t.http_status ?? "—"}</span>
                                {t.latency_ms != null && <span><Clock className="w-3 h-3 inline" /> {t.latency_ms}ms</span>}
                                {t.soft && <span className="text-amber-600">(soft — wpływa na score)</span>}
                              </div>
                            </div>
                            {expandedTests[t.test_id] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                          </button>
                          {expandedTests[t.test_id] && t.suggestion && (
                            <div className="px-4 pb-3 text-xs text-slate-700 border-t border-slate-200 pt-2">
                              <strong>Sugestia: </strong>{t.suggestion}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
